import Phaser from 'phaser';
import { GAME_WIDTH, GameLength, DEFAULT_GAME_LENGTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

export interface GameLengthSelectorCallbacks {
  onSelectionChanged: (length: GameLength) => void;
}

/**
 * Manages the Short (3pt) / Standard (7pt) game length selection.
 */
export class GameLengthSelector {
  private container: Phaser.GameObjects.Container;
  private buttons: Phaser.GameObjects.Container[] = [];
  private selectedLength: GameLength = DEFAULT_GAME_LENGTH;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: GameLengthSelectorCallbacks,
    initialLength: GameLength = DEFAULT_GAME_LENGTH
  ) {
    this.selectedLength = initialLength;
    this.container = this.scene.add.container(GAME_WIDTH / 2, 415);
    this.buildSelector();
  }

  private buildSelector(): void {
    // Label
    const label = this.scene.add
      .text(0, -15, 'Game Length', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    this.container.add(label);

    // Game length buttons (3 pts, 7 pts)
    const lengths: GameLength[] = [3, 7];
    const buttonWidth = 90;
    const spacing = 20;
    const totalWidth = lengths.length * buttonWidth + (lengths.length - 1) * spacing;
    const startX = -totalWidth / 2 + buttonWidth / 2;

    lengths.forEach((length, index) => {
      const x = startX + index * (buttonWidth + spacing);
      const btn = this.createButton(x, 15, length);
      this.container.add(btn);
      this.buttons.push(btn);
    });

    this.updateSelection();
  }

  private createButton(x: number, y: number, length: GameLength): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const isSelected = length === this.selectedLength;
    const labelText = length === 3 ? 'Short' : 'Standard';

    const bg = this.scene.add
      .rectangle(0, 0, 85, 38, isSelected ? PERSIAN_THEME.colors.gold : PERSIAN_THEME.colors.buttonBg)
      .setStrokeStyle(2, isSelected ? PERSIAN_THEME.colors.bronze : PERSIAN_THEME.colors.bronze);

    const text = this.scene.add
      .text(0, 0, labelText, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: isSelected ? PERSIAN_THEME.hex.feltGreen : PERSIAN_THEME.hex.textPrimary,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(85, 38);
    container.setInteractive({ useHandCursor: true });
    container.setData('length', length);
    container.setData('bg', bg);
    container.setData('text', text);

    container.on('pointerover', () => {
      if (this.selectedLength !== length) {
        bg.setFillStyle(PERSIAN_THEME.colors.buttonHover);
      }
    });

    container.on('pointerout', () => {
      if (this.selectedLength !== length) {
        bg.setFillStyle(PERSIAN_THEME.colors.buttonBg);
      }
    });

    container.on('pointerdown', () => {
      this.selectedLength = length;
      this.updateSelection();
      this.callbacks.onSelectionChanged(length);
    });

    return container;
  }

  private updateSelection(): void {
    this.buttons.forEach((btn) => {
      const length = btn.getData('length') as GameLength;
      const bg = btn.getData('bg') as Phaser.GameObjects.Rectangle;
      const text = btn.getData('text') as Phaser.GameObjects.Text;
      const isSelected = length === this.selectedLength;

      bg.setFillStyle(isSelected ? PERSIAN_THEME.colors.gold : PERSIAN_THEME.colors.buttonBg);
      text.setColor(isSelected ? PERSIAN_THEME.hex.feltGreen : PERSIAN_THEME.hex.textPrimary);
    });
  }

  getSelectedLength(): GameLength {
    return this.selectedLength;
  }

  setSelectedLength(length: GameLength): void {
    if (length !== this.selectedLength) {
      this.selectedLength = length;
      this.updateSelection();
    }
  }

  show(): void {
    this.container.setVisible(true);
  }

  hide(): void {
    this.container.setVisible(false);
  }

  /**
   * Get the container for crossfade animation.
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  destroy(): void {
    this.container?.destroy();
  }
}
